/**
 * ParentLinkConfirm — page that the CHILD lands on when they click the
 * "Confirm parental link" button in the email.
 *
 * Route: /parent-link/:token
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, XCircle, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { GlassCard, PageLoading } from '../components/shared';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => {
  const t = localStorage.getItem('noor_albayan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ParentLinkConfirm = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [decided, setDecided] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/parent/link-request/${token}`)
      .then((r) => setInfo(r.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Lien invalide.'));
  }, [token]);

  const decide = async (accept) => {
    if (!user) {
      // Redirect to login then come back here
      navigate(`/login?next=/parent-link/${token}`);
      return;
    }
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API_URL}/api/parent/link-request/${token}/decide`,
        { accept },
        { headers: headers() });
      setDecided(r.data.status);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur.");
    } finally { setBusy(false); }
  };

  if (authLoading) return <PageLoading />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <GlassCard className="max-w-md w-full text-center" data-testid="parent-link-error">
          <XCircle className="w-12 h-12 text-rose-400 mx-auto mb-3" />
          <h2 className="text-xl font-serif text-text-primary mb-2">Lien invalide</h2>
          <p className="text-sm text-text-secondary mb-5">{error}</p>
          <Link to="/" className="text-primary hover:underline">Retour à l'accueil</Link>
        </GlassCard>
      </div>
    );
  }

  if (!info) return <PageLoading />;

  if (decided) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <GlassCard className="max-w-md w-full text-center" data-testid="parent-link-decided">
          <CheckCircle2 className={`w-14 h-14 mx-auto mb-3 ${decided === 'confirmed' ? 'text-emerald-400' : 'text-text-muted'}`} />
          <h2 className="text-xl font-serif text-text-primary mb-2">
            {decided === 'confirmed' ? 'Lien confirmé 🌙' : 'Lien refusé'}
          </h2>
          <p className="text-sm text-text-secondary mb-5">
            {decided === 'confirmed'
              ? `${info.parent?.name || info.parent?.email} peut désormais suivre votre progression.`
              : "La demande a été refusée. Aucune donnée ne sera partagée."}
          </p>
          <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <GlassCard className="max-w-md w-full text-center" data-testid="parent-link-confirm">
        <div className="w-16 h-16 rounded-full bg-primary/15 flex items-center justify-center mx-auto mb-4">
          <Heart className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-xl font-serif text-text-primary mb-2">Demande de lien parental</h2>
        <p className="text-sm text-text-secondary mb-5 leading-relaxed">
          <strong>{info.parent?.name || info.parent?.email}</strong> souhaite être enregistré(e) comme parent/tuteur sur ton compte.
          Confirmer permettra à cette personne de voir ta progression, tes devoirs et le solde des crédits familiaux.
        </p>
        {!user && (
          <p className="text-xs text-amber-400 mb-4">
            Vous devez d'abord vous connecter à votre compte enfant pour décider.
          </p>
        )}
        <div className="flex gap-2">
          <Button onClick={() => decide(false)} disabled={busy} variant="outline" className="flex-1"
                  data-testid="parent-link-decline-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refuser'}
          </Button>
          <Button onClick={() => decide(true)} disabled={busy} className="flex-1 bg-primary text-background hover:bg-primary-hover"
                  data-testid="parent-link-accept-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accepter'}
          </Button>
        </div>
      </GlassCard>
    </div>
  );
};

export default ParentLinkConfirm;
